/**
 * XLSX export, written by hand like the GEXF one, and for the same reason:
 * a filtered list a reader cannot take away and re-sort is a claim, not
 * evidence. An .xlsx file is a ZIP of small XML parts, and a flat table
 * needs so few of them that a generator is less code than auditing a
 * spreadsheet library would be — the ZIP entries are stored uncompressed,
 * which costs some bytes and removes the only hard part.
 *
 * Numbers go into cells as numbers, not as formatted strings: the file is
 * for someone who wants to sum, sort and divide, and "1,2 млн. лв." would
 * take that away from them.
 */

/** How a column displays. The cell keeps the raw number either way:
    a money column holds 28507568.768732 — the sum of EUR awards converted
    at the fixed peg — and *shows* 28 507 568,77. Rounding the value
    instead would make the exported column disagree with the site's totals
    by exactly the digits that were thrown away. */
export type ColumnFormat = 'general' | 'money' | 'percent';

export interface XlsxSheet {
  name: string;
  header: readonly string[];
  rows: readonly (readonly (string | number | null | undefined)[])[];
  /** Per-column display formats, aligned with `header`. Omitted: general. */
  formats?: readonly (ColumnFormat | undefined)[];
}

/** One exportable column: a header and how to read the raw value. */
export interface ExportColumn<TRow> {
  header: string;
  value: (row: TRow) => string | number | null | undefined;
  format?: ColumnFormat;
}

export function sheetOf<TRow>(
  name: string,
  columns: readonly ExportColumn<TRow>[],
  rows: readonly TRow[],
): XlsxSheet {
  return {
    name,
    header: columns.map((column) => column.header),
    rows: rows.map((row) => columns.map((column) => column.value(row))),
    formats: columns.map((column) => column.format),
  };
}

/* ------------------------------------------------------------------ xml */

const escapeXml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  // control characters are invalid in XML 1.0 (tab and newline are not),
  // and a value pasted into a register can carry them
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

/** Worksheet names: ≤31 chars and none of []:*?/\ — Excel's rule, not ours. */
function sheetName(name: string, index: number): string {
  const clean = name.replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31).trim();
  return clean || `Лист${index + 1}`;
}

/** 0 -> A, 26 -> AA. */
function columnRef(index: number): string {
  let ref = '';
  for (let n = index; n >= 0; n = Math.floor(n / 26) - 1) {
    ref = String.fromCharCode(65 + (n % 26)) + ref;
  }
  return ref;
}

function cell(ref: string, value: string | number | null | undefined,
              style: number): string {
  const styleAttr = style ? ` s="${style}"` : '';
  if (value == null || value === '') return '';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    return `<c r="${ref}"${styleAttr}><v>${value}</v></c>`;
  }
  return `<c r="${ref}"${styleAttr} t="inlineStr">`
    + `<is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function worksheetXml(sheet: XlsxSheet): string {
  /* Column widths follow the content, so «Многопрофилна болница за активно
     лечение…» is not squeezed into an 8-character strip. Capped: one long
     tender title must not set a 200-character column. */
  const widths = sheet.header.map((header, column) => {
    let widest = header.length;
    for (const row of sheet.rows) {
      const value = row[column];
      if (value != null) widest = Math.max(widest, String(value).length);
    }
    return Math.min(60, Math.max(9, widest + 2));
  });
  const cols = widths.map((width, index) =>
    `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`).join('');

  /* Style indexes into STYLES_XML's cellXfs: 0 general, 1 bold header,
     2 money, 3 percent. A format applies to numbers only — a money column
     whose row reads "—" must not display the dash as 0,00 лв. */
  const styleFor = (column: number, value: string | number | null | undefined) => {
    if (typeof value !== 'number') return 0;
    const format = sheet.formats?.[column];
    if (format === 'money') return 2;
    if (format === 'percent') return 3;
    return 0;
  };

  const headerRow = `<row r="1">${sheet.header.map((label, index) =>
    cell(`${columnRef(index)}1`, label, 1)).join('')}</row>`;
  const bodyRows = sheet.rows.map((row, rowIndex) => {
    const r = rowIndex + 2;
    return `<row r="${r}">${row.map((value, index) =>
      cell(`${columnRef(index)}${r}`, value, styleFor(index, value))).join('')}</row>`;
  });

  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + `<cols>${cols}</cols>`
    + `<sheetData>${headerRow}${bodyRows.join('')}</sheetData>`
    + '</worksheet>';
}

/** Fonts and cell formats: general, the bold header row, money and percent.
    The money format shows two decimals because стотинки are real; the six
    trailing digits a peg conversion leaves on the value are display noise
    and stay hidden — but the full number remains in the cell, so a column
    sum in Excel still reproduces the site's totals exactly.
    numFmtId 164+ is the custom range; 0–163 are reserved built-ins. */
const STYLES_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
  + '<numFmts count="2"><numFmt numFmtId="164" formatCode="#,##0.00"/>'
  + '<numFmt numFmtId="165" formatCode="0.0%"/></numFmts>'
  + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>'
  + '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
  + '<fills count="2"><fill><patternFill patternType="none"/></fill>'
  + '<fill><patternFill patternType="gray125"/></fill></fills>'
  + '<borders count="1"><border/></borders>'
  + '<cellStyleXfs count="1"><xf/></cellStyleXfs>'
  + '<cellXfs count="4"><xf/><xf fontId="1" applyFont="1"/>'
  + '<xf numFmtId="164" applyNumberFormat="1"/>'
  + '<xf numFmtId="165" applyNumberFormat="1"/></cellXfs>'
  + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
  + '</styleSheet>';

function workbookXml(names: string[]): string {
  const sheets = names.map((name, index) =>
    `<sheet name="${escapeXml(name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join('');
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"'
    + ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + `<sheets>${sheets}</sheets></workbook>`;
}

function workbookRelsXml(count: number): string {
  const rels = Array.from({ length: count }, (_, index) =>
    `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/`
    + `officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`);
  rels.push(`<Relationship Id="rId${count + 1}" Type="http://schemas.openxmlformats.org/`
    + 'officeDocument/2006/relationships/styles" Target="styles.xml"/>');
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + `${rels.join('')}</Relationships>`;
}

function contentTypesXml(count: number): string {
  const overrides = Array.from({ length: count }, (_, index) =>
    `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType=`
    + '"application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>');
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
    + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType='
    + '"application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType='
    + '"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType='
    + '"application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + `${overrides.join('')}</Types>`;
}

const ROOT_RELS = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
  + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
  + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/'
  + 'relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';

/* ------------------------------------------------------------------ zip */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** When the file says it was made, in MS-DOS's packed format. */
function dosDateTime(stamp: Date): { date: number; time: number } {
  return {
    date: ((stamp.getFullYear() - 1980) << 9) | ((stamp.getMonth() + 1) << 5) | stamp.getDate(),
    time: (stamp.getHours() << 11) | (stamp.getMinutes() << 5) | (stamp.getSeconds() >> 1),
  };
}

/** A stored (method 0) ZIP: sizes and CRC are known, nothing is compressed. */
function zip(files: readonly { path: string; text: string }[]): Uint8Array {
  const encoder = new TextEncoder();
  const { date, time } = dosDateTime(new Date());
  const entries = files.map((file) => ({
    name: encoder.encode(file.path),
    data: encoder.encode(file.text),
  }));

  const localSize = entries.reduce((sum, e) => sum + 30 + e.name.length + e.data.length, 0);
  const centralSize = entries.reduce((sum, e) => sum + 46 + e.name.length, 0);
  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);

  let offset = 0;
  const offsets: number[] = [];
  const crcs: number[] = [];
  for (const entry of entries) {
    offsets.push(offset);
    const crc = crc32(entry.data);
    crcs.push(crc);
    view.setUint32(offset, 0x04034b50, true);
    view.setUint16(offset + 4, 20, true);            // version needed
    view.setUint16(offset + 6, 0x0800, true);        // UTF-8 names
    view.setUint16(offset + 8, 0, true);             // stored
    view.setUint16(offset + 10, time, true);
    view.setUint16(offset + 12, date, true);
    view.setUint32(offset + 14, crc, true);
    view.setUint32(offset + 18, entry.data.length, true);
    view.setUint32(offset + 22, entry.data.length, true);
    view.setUint16(offset + 26, entry.name.length, true);
    view.setUint16(offset + 28, 0, true);            // no extra field
    out.set(entry.name, offset + 30);
    out.set(entry.data, offset + 30 + entry.name.length);
    offset += 30 + entry.name.length + entry.data.length;
  }

  const centralStart = offset;
  entries.forEach((entry, index) => {
    view.setUint32(offset, 0x02014b50, true);
    view.setUint16(offset + 4, 20, true);            // made by
    view.setUint16(offset + 6, 20, true);            // version needed
    view.setUint16(offset + 8, 0x0800, true);
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, time, true);
    view.setUint16(offset + 14, date, true);
    view.setUint32(offset + 16, crcs[index]!, true);
    view.setUint32(offset + 20, entry.data.length, true);
    view.setUint32(offset + 24, entry.data.length, true);
    view.setUint16(offset + 28, entry.name.length, true);
    // extra, comment, disk, attributes internal and external: all zero
    view.setUint32(offset + 42, offsets[index]!, true);
    out.set(entry.name, offset + 46);
    offset += 46 + entry.name.length;
  });

  view.setUint32(offset, 0x06054b50, true);
  view.setUint16(offset + 8, entries.length, true);
  view.setUint16(offset + 10, entries.length, true);
  view.setUint32(offset + 12, offset - centralStart, true);
  view.setUint32(offset + 16, centralStart, true);
  return out;
}

/* ------------------------------------------------------------- assembly */

export function toXlsx(sheets: readonly XlsxSheet[]): Uint8Array {
  const names: string[] = [];
  sheets.forEach((sheet, index) => {
    let name = sheetName(sheet.name, index);
    // Excel refuses a workbook with two same-named sheets
    while (names.includes(name)) name = sheetName(`${name.slice(0, 28)}·${index + 1}`, index);
    names.push(name);
  });

  const files = [
    { path: '[Content_Types].xml', text: contentTypesXml(sheets.length) },
    { path: '_rels/.rels', text: ROOT_RELS },
    { path: 'xl/workbook.xml', text: workbookXml(names) },
    { path: 'xl/_rels/workbook.xml.rels', text: workbookRelsXml(sheets.length) },
    { path: 'xl/styles.xml', text: STYLES_XML },
    ...sheets.map((sheet, index) => ({
      path: `xl/worksheets/sheet${index + 1}.xml`,
      text: worksheetXml(sheet),
    })),
  ];
  return zip(files);
}

export function downloadXlsx(filename: string, sheets: readonly XlsxSheet[]) {
  const bytes = toXlsx(sheets);
  const blob = new Blob([bytes as BlobPart],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** „фирми“ -> „фирми-2026-07-31.xlsx“ — the day matters: these registers move. */
export function stampedFilename(base: string): string {
  return `${base}-${new Date().toISOString().slice(0, 10)}.xlsx`;
}
